import React, { useEffect, useRef, useState } from "react";

import {
  ChartDataParams,
  FormatChartData,
} from "../../../../components/recharts/functions";
import loader from "../../../../components/recharts/loader.gif";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../models/chart_model";
import { renderHourTick } from "../../../../components/recharts/components";
import { ReactComponent as Undo } from "../../../../icons/undo.svg";
import { UploadBtn } from "./DamAnalysis";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchModRtmDefault,
  // ModRtmData,
} from "../../../../Rest_api/DemoRestApi";
import { ModLoadDataUpdate } from "../../../../store/state/ModLoadState";
import { normalizeDisplayLabelWithoutUndefined } from "../../../../utility/displayText";

const chartSuffixes = [
  {
    suffix: "wt_mcp_rs_mwh",
    displayName: "Anticipated RTM Rate",
    stackSuffix: "wt_mcp_rs_mwh",
    chartType: ChartType.Line,
    stroke: "rgba(192, 65, 129, 1)",
    unit: "Rs/MWh",
    yAxisId: "left",
  },
  {
    suffix: "previous_mcp_rs_mwh",
    displayName: "Previous Day RTM Rate",
    stackSuffix: "previous_mcp_rs_mwh",
    chartType: ChartType.Line,
    stroke: "rgba(244, 114, 182, 1)",
    unit: "Rs/MWh",
    yAxisId: "left",
  },
  // {
  //   suffix: "sell_bid_mw",
  //   displayName: "Sell Bid",
  //   stackSuffix: "sell_bid_mw",
  //   chartType: ChartType.Line,
  //   unit: "MW",
  //   yAxisId: "left",
  // },
  // {
  //   suffix: "purchase_bid_mw",
  //   displayName: "Purchase Bid",
  //   stackSuffix: "purchase_bid_mw",
  //   chartType: ChartType.Line,
  //   unit: "MW",
  //   yAxisId: "left",
  // },
  // {
  //   suffix: "mcv_mw",
  //   displayName: "MCV",
  //   stackSuffix: "mcv_mw",
  //   chartType: ChartType.Bar,
  //   unit: "MW",
  //   yAxisId: "left",
  // },
];

const RtmPriceAnalysis = ({
  selectedBlock,
  data,
  loading,
  setData,
  message,
}: {
  selectedBlock: any[];
  data: any[];
  loading: boolean;
  setData: React.Dispatch<React.SetStateAction<any[]>>;
  message: string | null;
}) => {
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<any[]>([
    chartSuffixes[0],
    chartSuffixes[1],
    // chartSuffixes[2],
    // chartSuffixes[3],
  ]);
  const preSelectedDate = useSelector(
    (state: any) => state.modDemoDate.selectedDate,
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modDemoSession.modSession,
  );
  const dispatch = useDispatch();
  // Reset button
  const handleReset = async () => {
    try {
      const res = await fetchModRtmDefault(preSelectedDate, modSelectedSession);

      if (!res) return;

      const rtmDate = res.date;

      // Build payload for saving
      // const jsonUploadData = {
      //   date: preSelectedDate,
      //   session: modSelectedSession,
      //   data: res.data,
      // };

      // Transform for table/UI
      const transformedData = res.data.map((item: any) => ({
        time_slot: item.time_slot,
        date: rtmDate,
        wt_mcp_rs_mwh: item.mcp_rs_mwh,
        previous_mcp_rs_mwh: item.previous_mcp_rs_mwh,
      }));

      // Transform for redux state
      const dispatchData = res.data?.map((item: any) => {
        return {
          "Time Slot": item.time_slot,
          "Price (Rs/MWh)": item.mcp_rs_mwh,
        };
      });

      // Update redux
      dispatch(ModLoadDataUpdate({ type: "rtm", data: dispatchData }));

      // Update local state
      setData(transformedData);

      // Save modified demand data
      // await ModRtmData(jsonUploadData);
    } catch (err) {
      console.error("Reset failed:", err);
    }
  };
  useEffect(() => {
    let filteredData = data;
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock
          .map((item) => Number(item.value))
          .includes(Number(item.time_slot)),
      );
    }

    const par: ChartDataParams = {
      keys: ["time_slot", "date", "month"],
      tableKeys: ["date", "time_slot", "month"],
      groupBy: [],
      volumeKey: ``,
      aggregationKeys: ["wt_mcp_rs_mwh", "previous_mcp_rs_mwh"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [data, selectedBlock, selectedMetrics]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: string,
    stroke: string,
    unit: string,
    yAxisId: string,
  ) => {
    return {
      dataKey: `${key}_${suffix}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`,
      legendColor: undefined,
      type: chartType,
      stroke: stroke,
      unit: unit,
      yAxisId: yAxisId,
      stackId: stackSuffix !== undefined ? stackSuffix : undefined,
    };
  };

  function getLegends() {
    return selectedMetrics.flatMap(
      ({
        suffix,
        displayName,
        stackSuffix,
        chartType,
        stroke,
        unit,
        yAxisId,
      }) =>
        chartKeys.map(
          (key, index) =>
            createChartObject(
              key,
              index,
              suffix,
              displayName,
              stackSuffix,
              chartType,
              stroke,
              unit,
              yAxisId,
            ) as any,
        ),
    );
  }

  return (
    <>
      <div className="dash-card col-span-1 md:col-span-6 border border-[#718EBF1C] !bg-[#fff] !shadow-[rgba(149,157,165,0.2)_0px_8px_24px] !min-h-full w-full">
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={handleReset}
            className="flex items-center gap-1 text-sm  text-[#6F6D7C] font-medium"
          >
            <Undo className="w-4 h-4" />
            Reset
          </button>
          <div className="">
            <h2 className="chartHeading !text-[#34656D] !font-medium">
              Anticipated RTM Rate
            </h2>
          </div>
          <UploadBtn
            data={data}
            setData={setData}
            modalTitle="Upload RTM Data"
            title="Populate Anticipated RTM Rate"
          />
        </div>

        <>
          {loading ? (
            <div className="h-full w-full bg-white relative">
              <img
                src={loader}
                width={200}
                alt="loader"
                className="absolute inset-0 m-auto"
              />
            </div>
          ) : (
            <>
              {" "}
              {data.length === 0 ? (
                <>
                  <div className="flex justify-center items-center h-full">
                    <p className="text-gray-500 text-[14px]">
                      Kindly click the Upload button to upload data.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <ReMixChart
                    tooltipBoundary="viewport"
                    chartHeight="calc(100% - 50px)"
                    data={chartData}
                    showBrush={chartData.length > 96 ? true : undefined}
                    brushStart={BrushStart.End}
                    isTimeSlot={true}
                    onlyTitle={true}
                    legends={getLegends()}
                    nameGap={40}
                    top={10} // Top margin for grid
                    bottom={30} // Bottom margin for grid
                    yAxisLabel="Rs/MWh"
                    xDataKey={"time_slot"}
                    xTick={(value: any, index: any) => {
                      return renderHourTick(value, 10);
                    }}
                    secondXDataKey={"date"}
                    showToolbox={false}
                  />
                  <span className="text-zinc-500 text-[10px] mb-1">
                    {message && <>* {message}</>}
                  </span>
                </>
              )}
            </>
          )}
        </>
      </div>
    </>
  );
};

export default RtmPriceAnalysis;
